import type { DeviceName } from '@susu/headless-commerce/components/ResponsiveImage/ResponsiveImage.types'
import { IMAGE_CDN_BASE_URL } from '@susu/headless-commerce/config/config'
import type { Locale } from '@susu/headless-commerce/config/locale'
import type { SiteId } from '@susu/headless-commerce/gql/generated/graphql'
import { convertLocaleToCommerceGraphQLFormat } from '@susu/headless-commerce/utils/localeUtils'
import log from '@susu/headless-commerce/utils/log'
import { getDecimalFromWholeNumber } from '@susu/headless-commerce/utils/numbers'
import { isNotNull } from '@susu/null'
import { isNotUndefined } from '@susu/undefined'
import type Sizes from 'responsive-image-utils/dist/types/interface/Sizes'
import type { Selected } from '../domain/Selection/Selection'
import type { previewCloudinaryConfigurationType } from '../domain/Step/StepDataAccessObject'

export type LayerImageProps = {
  modelId: string
  productIds: string[]
  siteId: SiteId
  locale: Locale
}

/**
 * An object that caches the transformations for layered images
 */
const transformationCache: Selected = {}

/**
 * An object that contains arrays of widths for different device types.
 */
export const srcSetWidths = {
  mobile: [375, 480, 768, 828, 1024],
  tablet: [768, 920, 1300, 1440, 1920],
  desktop: [768, 1296, 1728, 2300, 3000, 3500],
}
// Constant multiplier to ensure the image is sharp.
export const IMAGE_QUALITY_MULTIPLIER = 2.5

/**
 * Generates the source sets for cropped images based on the provided URL, sizes, and category step.
 *
 * @param url - The URL of the image.
 * @param sizes - The sizes object containing the viewport widths.
 * @param deviceName - The name of the device.
 * @param categoryStep - The category step for the image.
 * @returns A string containing the deviceSrcSet.
 */
export const deviceSrcSet = ({
  url,
  sizes,
  deviceName,
  categoryStep,
  previewCloudinaryConfiguration,
}: {
  url: string
  sizes: Sizes
  deviceName: DeviceName
  categoryStep: string
  previewCloudinaryConfiguration: previewCloudinaryConfigurationType
}) => {
  const { aspectRatio } = previewCloudinaryConfiguration
  const URLParts = url.split('/')
  const sizedParts = URLParts.filter(
    (bit) => bit.includes('h_') || bit.includes('w_'),
  )
  const lastSizedPart = sizedParts.at(-1) ?? ''
  const transformationBits = lastSizedPart.split(',').sort()
  const widthIndex = transformationBits.findIndex((bit) => bit.startsWith('w_'))
  const heightIndex = transformationBits.findIndex((bit) =>
    bit.startsWith('h_'),
  )

  const deviceSizeMap = {
    desktop: sizes.lg,
    mobile: sizes.xs,
    tablet: sizes.md,
  }
  const widths = srcSetWidths[deviceName]
  const factor = getDecimalFromWholeNumber(
    Number(deviceSizeMap[deviceName].replace('vw', '')),
  )

  return widths
    .map((width) => {
      const widthValue = Math.round(width * factor * IMAGE_QUALITY_MULTIPLIER)
      const heightReplacement = `${Math.round(widthValue * aspectRatio)}`
      const widthReplacement =
        categoryStep === 'shoes' ? `${widthValue},x_60` : `${widthValue}`
      const transformationBitsCopy = [...transformationBits]

      transformationBitsCopy[widthIndex] = `w_${widthReplacement}`
      transformationBitsCopy[heightIndex] = `h_${heightReplacement}`

      return url
        .replace(lastSizedPart, transformationBitsCopy.sort().join(','))
        .replace('.jpg', `.jpg ${width}w`)
    })
    .join(',')
}

/**
 * Generates the source sets for cropped images based on the provided URL, sizes, and category step.
 *
 * @param url - The URL of the image.
 * @param sizes - The sizes object containing the viewport widths.
 * @param categoryStep - The category step for the image.
 * @param previewCloudinaryConfiguration - The previewCloudinaryConfiguration object containing the configuration for the image.
 * @returns An object containing the desktopSrcSet, mobileSrcSet, and tabletSrcSet.
 */
export const generateCroppedImagesSrcSets = (
  url: string,
  sizes: Sizes,
  categoryStep: string,
  previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
) => {
  // TODO-1.4: previewCloudinaryConfiguration
  return {
    desktopSrcSet: deviceSrcSet({
      url,
      sizes,
      deviceName: 'desktop',
      categoryStep,
      previewCloudinaryConfiguration,
    }),
    mobileSrcSet: deviceSrcSet({
      url,
      sizes,
      deviceName: 'mobile',
      categoryStep,
      previewCloudinaryConfiguration,
    }),
    tabletSrcSet: deviceSrcSet({
      url,
      sizes,
      deviceName: 'tablet',
      categoryStep,
      previewCloudinaryConfiguration,
    }),
  }
}

/**
 * Generates the URL for the composed layer API.
 *
 * @param {LayerImageProps} props - The props object containing the modelId, productIds, siteId, and locale.
 * @returns string with the URL for the composed layer API.
 */
export const composeLayerAPIUrl = ({
  modelId,
  productIds,
  siteId,
  locale,
}: LayerImageProps): string => {
  const loc = convertLocaleToCommerceGraphQLFormat(locale)

  return `${process.env.NEXT_PUBLIC_COMPOSED_LAYER_API_BASE_URL}/api/composedUrl?siteID=${siteId}&locale=${loc}&ImageCheck=true&ModelLayer=${modelId}&Products=${productIds.join(
    ',',
  )}`
} // Yes

/**
 * Fetches the transformations for layered images from the composed layer API.
 *
 * @param {LayerImageProps} props - The props object containing the modelId, productIds, siteId, and locale.
 * @returns string with the transformations for layered images.
 */
export const fetchLayerImageTransformation = async (
  props: LayerImageProps,
): Promise<string> => {
  const apiURL = composeLayerAPIUrl(props)

  const cachedEntry = transformationCache[apiURL]
  if (isNotUndefined(cachedEntry) && isNotNull(cachedEntry)) {
    return cachedEntry
  }

  const response = await fetch(apiURL)
  if (!response.ok) {
    const responseJSON = await response.json()
    throw new Error(responseJSON.error.message)
  }

  const data = await response.text()

  transformationCache[apiURL] = data

  return data
} // Yes

/**
 * Generates the URL for the layered image.
 *
 * @param {LayerImageProps} props - The props object containing the modelId, productIds, siteId, and locale.
 * @param {string} categoryStep - The category step for the image.
 * @returns string with the URL for the layered image.
 */
export const getImageUrl = async (
  props: LayerImageProps,
  categoryStep: string,
  previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
): Promise<string> => {
  const transformation = await fetchLayerImageTransformation(props)
  const adjusted = adjustLayeredTransform(
    `${IMAGE_CDN_BASE_URL}${transformation}`,
    categoryStep,
    previewCloudinaryConfiguration,
  )

  log.debug({ method: 'getImageUrl', adjusted })

  return adjusted
} // Yes

/**
 * Adjusts the transformation for the category step.
 *
 * @param {string} url - The URL of the image.
 * @param {string} categoryStep - The category step for the image.
 * @param {previewCloudinaryConfigurationType} previewCloudinaryConfiguration - The previewCloudinaryConfiguration object containing the configuration for the image.
 * @returns string with the adjusted transformation for the category step.
 */
export const adjustLayerCategoryTransformation = (
  url: string,
  categoryStep: string,
  previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
) => {
  const { urlTransformations, gravityParameter } =
    previewCloudinaryConfiguration
  const URLParts = url.split('/')

  const cropPart = URLParts.find((part) => part.includes('c_pad'))
  const cropIndex = cropPart ? URLParts.indexOf(cropPart) : URLParts.length - 1
  const cropBits = cropPart ? cropPart.split(',') : []
  const newCropBits = [
    ...cropBits.filter((bit) => !bit.startsWith('c_')),
    ...gravityParameter.split(','),
  ].sort()

  URLParts[cropIndex] = newCropBits.join(',')

  if (urlTransformations !== '') {
    const transformRefPart = URLParts.find((part) => part.endsWith('y_0'))
    const transformRefIndex = transformRefPart
      ? URLParts.indexOf(transformRefPart)
      : URLParts.length - 1

    URLParts.splice(transformRefIndex + 1, 0, urlTransformations)

    log.debug({
      method: 'adjustLayerCategoryTransformation',
      transformRefPart,
      transformRefIndex,
    })
  }

  log.debug({
    method: 'adjustLayerCategoryTransformation',
    url,
    URLParts,
    padPart: cropPart,
    padIndex: cropIndex,
  })

  return URLParts.join('/')
}

/**
 * Adjusts the transformation for the size of the image.
 *
 * @param {Array<string>} transformBits - The transformation bits for the image.
 * @param {string} categoryStep - The category step for the image.
 * @returns Array<string> with the adjusted transformation for the size of the image.
 */
export const adjustLayerSizeTransformation = (
  transformBits: Array<string>,
  categoryStep: string,
  previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
): Array<string> => {
  const { aspectRatio } = previewCloudinaryConfiguration
  const bitsCopy = [...transformBits, 'q_auto'].sort()
  const widthIndex = bitsCopy.findIndex((bit) => bit.startsWith('w_'))
  const heightIndex = bitsCopy.findIndex((bit) => bit.startsWith('h_'))

  const [baseWidth] = srcSetWidths.mobile
  const adjustedHeight = Math.round(baseWidth * aspectRatio)

  bitsCopy[widthIndex !== -1 ? widthIndex : bitsCopy.length] = `w_${baseWidth}`
  bitsCopy[heightIndex !== -1 ? heightIndex : bitsCopy.length] =
    `h_${adjustedHeight}`

  return bitsCopy
}

/**
 * Adjusts the transformation for the layered image.
 *
 * @param {string} url - The URL of the image.
 * @param {string} categoryStep - The category step for the image.
 * @param {string} previewCloudinaryConfiguration - Preview cloudinary configuration
 * @returns string with the adjusted transformation for the layered image.
 */
export const adjustLayeredTransform = (
  url: string,
  categoryStep: string,
  previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
): string => {
  const adjustedUrl = url.replaceAll('f6f6f6', 'efefef')
  const URLParts = adjustedUrl.split('/')
  const borderPart = URLParts.find((part: string) => part.includes('bo_'))
  const sizedParts = URLParts.filter(
    (bit) => bit.includes('h_') || bit.includes('w_'),
  )
  const lastSizedPart = sizedParts.at(-1)

  const transformBits =
    lastSizedPart
      ?.split(',')
      .filter(
        (bit) =>
          !['q_', 'dpr_', 'f_', 'fl_', 'c_'].some((key) => bit.includes(key)),
      ) ?? []
  const newBits = adjustLayerSizeTransformation(
    transformBits,
    categoryStep,
    previewCloudinaryConfiguration,
  )

  const newTransform = [...newBits, 'c_pad', 'fl_progressive'].sort()
  const borderless = borderPart
    ? adjustedUrl.replace(`${borderPart}/`, '')
    : adjustedUrl
  const newUrl = lastSizedPart
    ? borderless.replace(lastSizedPart, newTransform.join(','))
    : borderless
  const formatLess = newUrl.slice(0, -4)

  log.debug({ method: 'adjustLayeredTransform', url, newUrl, formatLess })

  return adjustLayerCategoryTransformation(
    `${formatLess}.jpg`,
    categoryStep,
    previewCloudinaryConfiguration,
  )
}
